import React, { ChangeEvent } from 'react';
import styled from 'styled-components';
import { StyledFilterLabel } from '../Text/StyledText';
import { BellaTestIds } from '../../../../common/qaIds';

const RadioButtonContainer = styled.div<{ hasErrors?: boolean }>`
    display: flex;
    min-width: 20px;
    width: 20px;
    height: 20px;
    position: relative;
    &:before {
        content: '';
        border-radius: 100%;
        border: 1px solid ${({ theme, hasErrors }) => (hasErrors ? theme.colors.red90 : theme.colors.charcoal40)};
        background: none;
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        box-sizing: border-box;
        pointer-events: none;
        z-index: 0;
    }
`;

const StyledRadioButton = styled.input`
    opacity: 0;
    z-index: 2;
    position: absolute;
    width: 100%;
    height: 100%;
    margin: 0;
    cursor: pointer;
    &:checked ~ div {
        width: calc(100% - 10px);
        height: calc(100% - 10px);
        transition: width 0.2s ease-out, height 0.2s ease-out;
    }
`;

const RadioButtonFill = styled.div<{ hasErrors?: boolean }>`
    background: ${({ theme, hasErrors }) => (hasErrors ? theme.colors.red90 : theme.colors.charcoal40)};
    width: 0;
    height: 0;
    border-radius: 100%;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.2s ease-in, height 0.2s ease-in;
    pointer-events: none;
    z-index: 1;
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: row;
`;

interface Props {
    text: string;
    value: string;
    isChecked: boolean;
    onChange: (isChecked: boolean, value: string) => void;
    className?: string;
}

const RadioButton: React.SFC<Props> = ({ className, text, value, isChecked, onChange }) => {
    const id = `radio-${value}`;
    return (
        <Wrapper className={className} data-testid={BellaTestIds.radioBtnLabel}>
            <RadioButtonContainer>
                <StyledRadioButton
                    id={id}
                    type="radio"
                    value={value}
                    checked={isChecked}
                    onChange={() => onChange(!isChecked, value)}
                />
                <RadioButtonFill />
            </RadioButtonContainer>
            <StyledFilterLabel htmlFor={id} textType="button">
                {text}
            </StyledFilterLabel>
        </Wrapper>
    );
};

interface RadioButtonFormProps {
    text: string;
    value: string;
    isChecked: boolean;
    name: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    className?: string;
    hasErrors?: boolean;
}

export const RadioButtonForm: React.SFC<RadioButtonFormProps> = ({
    className,
    text,
    value,
    name,
    isChecked,
    onChange,
    hasErrors,
}) => {
    const id = `radio-${value}`;
    return (
        <Wrapper className={className}>
            <RadioButtonContainer hasErrors={hasErrors}>
                <StyledRadioButton
                    id={id}
                    type="radio"
                    value={value}
                    checked={isChecked}
                    onChange={onChange}
                    name={name}
                />
                <RadioButtonFill hasErrors={hasErrors} />
            </RadioButtonContainer>
            <StyledFilterLabel hasErrors={hasErrors} htmlFor={id} textType="button">
                {text}
            </StyledFilterLabel>
        </Wrapper>
    );
};

export default RadioButton;
